// ============================================================================
// formRules.js — shared antd validation rules + normalizers (v2, E.164)
// Location: ClientApp/shared/formRules.js
// Forms Streamlining build (5 Aug 2026)
//
// ONE module imported by CareersApp.jsx, MyOnboardingApp.jsx and (next drop)
// OnboardingApp.jsx so the UIs can never drift. Error PREVENTION over error
// reporting: text rules ship with `normalize` transforms so bad input is
// corrected as the user types.
//
// v2 changes (client decision, 5 Aug 2026):
//   • Phones are stored in E.164 (+64221545721) with a COMPULSORY country
//     code — validation via libphonenumber-js (npm i libphonenumber-js),
//     paired with the shared <PhoneField /> country-selector input.
//   • Tiered capitalisation: personName (Title Case, preserves McDonald /
//     O'Brien-Smith / Māori macrons) and addressCase (keeps PO / RD / CBD
//     and leading-digit tokens as typed).
//
// Usage:
//   import { rules, normalizers, helpers } from '../shared/formRules';
//   <Form.Item name="firstName" rules={[rules.required, rules.personName]}
//              normalize={normalizers.titleCase}> ...
//   <Form.Item name={['personal','mobile']} rules={[rules.required, rules.phoneValid]}>
//       <PhoneField />
//   </Form.Item>
//
// Developed & Architected by Sachin Singh — E-Business
// www.ebusinesserp.com  sachin@ebusinesserp.com
// ============================================================================

import { parsePhoneNumberFromString } from 'libphonenumber-js';

// ---------------------------------------------------------------------------
// Normalizers — antd Form.Item `normalize` (value, prevValue) => value
// ---------------------------------------------------------------------------

/** Title Case for personal names. Handles Māori macrons, hyphens
 *  (Smith-Jones), apostrophes (O'Brien); preserves deliberate interior
 *  capitals (McDonald, DeSouza); collapses double spaces. */
function titleCase(value) {
    if (value == null) return value;
    const s = String(value).replace(/\s{2,}/g, ' ');
    return s.replace(/([^\s\-']+)/g, (word) => {
        if (!word) return word;
        if (/[A-ZĀĒĪŌŪ]/.test(word.slice(1))) {
            return word.charAt(0).toUpperCase() + word.slice(1);
        }
        return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
    });
}

/** Lighter Title Case for addresses / suburbs / workplaces: words starting
 *  with a digit ("525", "12b") and short ALL-CAPS tokens (PO, RD, CBD, NZ)
 *  pass through as typed. */
function addressCase(value) {
    if (value == null) return value;
    const s = String(value).replace(/\s{2,}/g, ' ');
    return s
        .split(' ')
        .map((w) => {
            if (!w) return w;
            if (/^\d/.test(w)) return w;               // 525, 12b
            if (/^[A-Z]{2,3}$/.test(w)) return w;      // PO, RD, CBD
            return titleCase(w);
        })
        .join(' ');
}

/** Keep digits only, optionally capped at maxLen. Returns a normalizer fn. */
function digitsOnly(maxLen) {
    return (value) => {
        if (value == null) return value;
        let d = String(value).replace(/\D+/g, '');
        if (maxLen && d.length > maxLen) d = d.slice(0, maxLen);
        return d;
    };
}

/** Trim + lowercase (emails). */
function emailNorm(value) {
    if (value == null) return value;
    return String(value).trim().toLowerCase();
}

/** Uppercase, strip spaces (tax codes). */
function upper(value) {
    if (value == null) return value;
    return String(value).toUpperCase().replace(/\s+/g, '');
}

/** NZ driver licence number: 2 letters + 6 digits (AB123456). Uppercases,
 *  strips anything else, caps at 8 characters (client UAT, Oct 2026). */
function licenceNumberNorm(value) {
    if (value == null) return value;
    const s = String(value).toUpperCase().replace(/[^A-Z0-9]/g, '');
    return s.slice(0, 8);
}

/** COA number: digits with ONE hyphen after the first two (12-345678).
 *  Hyphen is inserted automatically; capped at 9 characters incl. hyphen. */
function coaNumberNorm(value) {
    if (value == null) return value;
    const d = String(value).replace(/\D/g, '').slice(0, 8);
    if (d.length <= 2) return d;
    return `${d.slice(0, 2)}-${d.slice(2)}`;
}

// ---------------------------------------------------------------------------
// Helpers — plain predicates/converters, also usable outside antd
// ---------------------------------------------------------------------------

/** Parse anything (E.164, national, messy) → E.164 string or null. */
function toE164(raw, defaultCountry = 'NZ') {
    if (!raw) return null;
    const p = parsePhoneNumberFromString(String(raw).trim(), defaultCountry);
    return p ? p.number : null;
}

/** Human display: "+64 21 123 4567". Falls back to the raw value. */
function formatIntl(raw, defaultCountry = 'NZ') {
    if (!raw) return raw;
    const p = parsePhoneNumberFromString(String(raw).trim(), defaultCountry);
    return p ? p.formatInternational() : String(raw);
}

/** True when the value parses as a REAL number for its country. */
function isValidPhone(raw, defaultCountry = 'NZ') {
    if (!raw) return false;
    const p = parsePhoneNumberFromString(String(raw).trim(), defaultCountry);
    return !!(p && p.isValid());
}

/** Conservative NZ-mobile check for the swap-on-save helper (02x… / +642x…).
 *  Deliberately regex-based: only ever auto-moves a number when it is
 *  unmistakably an NZ mobile. */
function isNzMobile(raw) {
    if (!raw) return false;
    const d = String(raw).replace(/[^\d+]/g, '');
    return /^(02\d{7,9}|\+?642\d{7,9})$/.test(d);
}

/** IRD: 8 or 9 digits after stripping (8-digit numbers are zero-padded at
 *  render time — Ir330PdfService handles this). */
function isValidIrd(raw) {
    if (!raw) return false;
    const d = String(raw).replace(/\D/g, '');
    return d.length === 8 || d.length === 9;
}

function isAtLeastAge(dateStr, minYears) {
    if (!dateStr) return false;
    const dob = new Date(dateStr);
    if (isNaN(dob.getTime())) return false;
    const cutoff = new Date();
    cutoff.setFullYear(cutoff.getFullYear() - minYears);
    return dob <= cutoff;
}

/** Careers gender labels → portal M/F codes (null when no clean mapping). */
function mapGender(raw) {
    if (!raw) return null;
    switch (String(raw).trim().toUpperCase()) {
        case 'MALE': case 'M': return 'M';
        case 'FEMALE': case 'F': return 'F';
        default: return null;
    }
}

// ---------------------------------------------------------------------------
// antd rules
// ---------------------------------------------------------------------------

const required = { required: true, message: 'Required' };

const personName = {
    pattern: /^[A-Za-zĀĒĪŌŪāēīōū'\- ]{1,60}$/,
    message: 'Letters, spaces, hyphens and apostrophes only',
};

const irdNumber = {
    validator: (_, v) => {
        if (v == null || v === '') return Promise.resolve(); // pair with `required` where mandatory
        return isValidIrd(v)
            ? Promise.resolve()
            : Promise.reject(new Error('IRD number must be 8 or 9 digits'));
    },
};

/** Valid international phone (any country) — optional-friendly: pair with
 *  `rules.required` where the field is mandatory. Values arrive from
 *  <PhoneField /> already carrying the country code. */
const phoneValid = {
    validator: (_, v) => {
        if (v == null || v === '') return Promise.resolve();
        const s = String(v).trim();
        // Prefer E.164; fall back to NZ so legacy national values saved before
        // this build ("0221545721") still validate without re-typing.
        const p = parsePhoneNumberFromString(s) || parsePhoneNumberFromString(s, 'NZ');
        return p && p.isValid()
            ? Promise.resolve()
            : Promise.reject(new Error('Enter a valid phone number, including country code'));
    },
};

const postcode4 = {
    pattern: /^\d{4}$/,
    message: 'Postcode is 4 digits',
};

const emailRule = {
    type: 'email',
    message: 'Enter a valid email address',
};

const dobMin16 = {
    validator: (_, v) => {
        if (v == null || v === '') return Promise.resolve();
        return isAtLeastAge(v, 16)
            ? Promise.resolve()
            : Promise.reject(new Error('Applicants must be at least 16 years old'));
    },
};

// NZ bank account parts: BB-bbbb-AAAAAAA(A)-SS(SS)
const bankCode2   = { pattern: /^\d{2}$/,    message: '2 digits' };
const branchCode4 = { pattern: /^\d{4}$/,    message: '4 digits' };
const accountNum  = { pattern: /^\d{7,8}$/,  message: '7–8 digits' };
const suffix24    = { pattern: /^\d{2,4}$/,  message: '2–4 digits' };

// Licence / COA formats (client UAT, Oct 2026) — shared by Careers + My Onboarding
const nzLicenceNumber = {
    pattern: /^[A-Z]{2}\d{6}$/,
    message: 'Licence number is 2 letters followed by 6 digits (e.g. AB123456)',
};
const cardVersion3 = {
    pattern: /^\d{3}$/,
    message: 'Card version is exactly 3 digits',
};
const coaNumber = {
    pattern: /^\d{2}-\d{5,6}$/,
    message: 'COA number looks like 12-345678 (max 9 characters including the hyphen)',
};
/** Candidate account password (client UAT, Oct 2026): 8+ characters with an
 *  uppercase letter, a number and a symbol. Mirrored server-side in
 *  CandidatePortalEndpoints.IsStrongPassword. */
const strongPassword = {
    pattern: /^(?=.*[A-Z])(?=.*\d)(?=.*[^A-Za-z0-9]).{8,}$/,
    message: 'At least 8 characters, including an uppercase letter, a number and a symbol',
};

/** A real calendar date that is today or later (COA expiry, start dates). */
const futureDate = {
    validator: (_, v) => {
        if (v == null || v === '') return Promise.resolve();
        const s = typeof v === 'string' ? v : (v && typeof v.format === 'function' ? v.format('YYYY-MM-DD') : String(v));
        const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(s);
        if (!m) return Promise.reject(new Error('Enter a valid date'));
        const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
        if (isNaN(d.getTime()) || d.getFullYear() !== Number(m[1]) || d.getMonth() !== Number(m[2]) - 1 || d.getDate() !== Number(m[3]))
            return Promise.reject(new Error('That date does not exist — please check it'));
        const today = new Date(); today.setHours(0, 0, 0, 0);
        return d >= today ? Promise.resolve() : Promise.reject(new Error('This date has already passed'));
    },
};

export const normalizers = { titleCase, addressCase, digitsOnly, emailNorm, upper, licenceNumberNorm, coaNumberNorm };
export const helpers = {
    toE164, formatIntl, isValidPhone, isNzMobile,
    isValidIrd, isAtLeastAge, mapGender,
};
export const rules = {
    required, personName, irdNumber, phoneValid, postcode4,
    email: emailRule, dobMin16, bankCode2, branchCode4, accountNum, suffix24,
    nzLicenceNumber, cardVersion3, coaNumber, futureDate, strongPassword,
};
export default { rules, normalizers, helpers };
